/**
 * @file src/views/VID/views/helpers/getVidSteps.ts
 * @exports getVidSteps
 * @summary Dynamically constructs the sequence of steps for the VID verification process based on configuration options, with a selfie-only sequence for identity re-checks.
 * @remarks Modifies the default VID steps based on features like SSN collection, additional custom steps, disclosure requirements, and liveness checking.
 * @example
 * ```typescript
 * const steps = getVidSteps({
 *   collectSSN: true,
 *   showDisclosuresStep: true,
 *   skipLiveness: false,
 *   additionalSteps: [customStep1, customStep2]
 * });
 * // Returns customized array of StepType objects for the VID flow
 * ```
 * @see defaultVidSteps
 */
import { StepType } from "@cerebruminc/cerebellum";
import { defaultVidSteps, VidStepsEnum } from "./../../const";

interface GetVidSteps {
	additionalSteps?: StepType[];
	collectSSN: boolean;
	isIdentityRecheck?: boolean;
	// This will show the disclosures step, even when the applicant doesn't have to sign them. For packages that always have disclosures, or only location based disclosures.
	showDisclosuresStep: boolean;
	skipLiveness?: boolean;
}
export const getVidSteps = ({
	additionalSteps,
	collectSSN,
	isIdentityRecheck,
	showDisclosuresStep,
	skipLiveness,
}: GetVidSteps) => {
	if (isIdentityRecheck) {
		return defaultVidSteps.filter((step) => step.id === VidStepsEnum.Selfie);
	}

	const steps: StepType[] = [...defaultVidSteps];

	if (!collectSSN) {
		steps.splice(2, 1);
	}

	if (additionalSteps && additionalSteps.length > 0 && collectSSN) {
		steps.splice(3, 0, ...additionalSteps);
	} else if (additionalSteps && additionalSteps.length > 0 && !collectSSN) {
		steps.splice(2, 0, ...additionalSteps);
	}

	if (!showDisclosuresStep) {
		// remove Disclosures step
		steps.pop();
	}

	if (skipLiveness) {
		steps.splice(0, 1);
	}

	return steps;
};
